import { useEffect, useRef } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import type { GridController } from "../../focus/gridController";
import type { DataGridInstance } from "../../types";

/** Ask for the next batch while this many loaded rows are still below the range. */
const LOAD_MORE_THRESHOLD = 10;

interface UseLoadMoreInput<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
    rowCount: number;
    rangeEndIndex: number;
    /** The focused row counts as reached before the scroll catches up (End). */
    getFocusedIndex: () => number;
}

/**
 * Load more on scroll: calls `loadMore.onLoadMore` when the rendered range
 * or the focused row reaches the last `LOAD_MORE_THRESHOLD` rows, while more
 * rows exist and the next batch is neither loading nor failed. It asks once
 * per list and row count until the batch starts loading, so a caller that
 * never reports `isLoading` still gets one call. A new `resetKey` scrolls
 * back to the top.
 */
export const useLoadMore = <TData extends RowData>({
    instance,
    controller,
    rowCount,
    rangeEndIndex,
    getFocusedIndex,
}: UseLoadMoreInput<TData>) => {
    const { loadMore, optionsRef, table } = instance;
    const hasMore = loadMore?.hasMore ?? false;
    const isLoading = loadMore?.isLoading ?? false;
    const isError = loadMore?.isError ?? false;
    const resetKey = loadMore?.resetKey;
    const nearEndIndex = rowCount - LOAD_MORE_THRESHOLD;
    const isFocusNearEnd = useSelector(
        table.atoms.cellSelection,
        () => getFocusedIndex() >= nearEndIndex,
    );
    const isNearEnd = rangeEndIndex >= nearEndIndex || isFocusNearEnd;
    const requestKey = `${resetKey ?? ""}:${rowCount}`;
    const requestedKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (isLoading) {
            requestedKeyRef.current = null;
            return;
        }
        if (
            !hasMore ||
            isError ||
            !isNearEnd ||
            requestedKeyRef.current === requestKey
        ) {
            return;
        }
        requestedKeyRef.current = requestKey;
        optionsRef.current.loadMore?.onLoadMore();
    }, [hasMore, isLoading, isError, isNearEnd, requestKey, optionsRef]);

    const previousResetKeyRef = useRef(resetKey);
    useEffect(() => {
        if (previousResetKeyRef.current === resetKey) {
            return;
        }
        previousResetKeyRef.current = resetKey;
        controller.viewport?.scrollTo({ top: 0 });
    }, [resetKey, controller]);
};
